import { useState } from 'react';
import { Button, Card, Flex, Input, InputNumber, Select, Typography, theme } from 'antd';
import { LuPlus, LuTrash2 } from 'react-icons/lu';

import { DEFAULT_CUSTOM_PRICE_LABEL } from '@routes/userRoutes/projects/singleProject/components/modifyLayers/hexagonColorFilter/matrixFilter/matrixClassification';
import { formatArea } from '@utils/measurementSystem';
import { DEFAULT_CURRENCY } from '../../constants';
import { createClassId } from '../../utils/classId';
import {
	buildAddClassPatch,
	buildRemoveClassPatch,
	buildUpdateClassPatch,
} from '../../utils/classMutations';
import ColorDot from './ColorDot';
import ClassEditRow from './ClassEditRow';
import ClassRow from './ClassRow';

const { Text } = Typography;

const EMPTY_CUSTOM_PRICE_FIELDS = [];

/**
 * Cost summary: per-class detected object area × price, plus an editable list
 * of custom flat-rate price fields (applied to the total classified object
 * area, e.g. mobilization cost). Identical math for both filter modes — a class
 * here is always `{ id, label, color, pricePerM2 }` regardless of Matrix vs
 * Simple — so this is a single shared component rather than duplicated
 * per-editor logic (which is how the previous version had it).
 *
 * Pricing reads `objectArea` (the summed `sum_object_area` of the detections in
 * each class), never the container `area` — hexagon resolution or DSP image
 * footprint — which is what the classification share percentages are measured
 * against but is not what gets priced.
 *
 * ## Class editing
 *
 * Classes can be added, renamed, recolored, repriced and removed here as well as
 * in the editors, so the pricing column is self-sufficient — the common task of
 * "adjust what this class costs" no longer means crossing back to the other side
 * of the drawer.
 *
 * Rows are read-only until explicitly opened for editing, unlike the custom price
 * rows below them, which are always live inputs. Two reasons: a class row also
 * has to display its computed `object area × price` breakdown and cost, which inputs
 * would crowd out; and class edits recolor the map, so committing on an explicit
 * save avoids reclassifying on every keystroke. Edits are held in local draft
 * state and only reach the filter on save — cancel discards them.
 *
 * Add/remove delegate to `classMutations`, which keeps the matrix threshold grid
 * in step (see that module). Range editing stays in SimpleEditor and boundary
 * editing in MatrixEditor: both are mode-specific, and this component is shared.
 *
 * @param {object} props
 * @param {object} props.filter - active matrix/simple filter config
 * @param {(updates: object) => void} props.updateFilter
 * @param {Object} [props.classificationResults] - per-class
 *   `{ area, objectArea, ... }` from useHexagonClassification, where `area` is
 *   container area and `objectArea` is detected object area
 * @param {{id,symbol,label}} [props.currency] - resolved currency for price labels
 * @param {Array<{id,symbol,label,flag}>} [props.currencies] - options for the header's
 *   currency picker
 */
const CostSummary = ({
	filter,
	updateFilter,
	classificationResults = {},
	currency = DEFAULT_CURRENCY,
	currencies = [],
}) => {
	const { token } = theme.useToken();
	const classes = filter.classes ?? [];
	const customPriceFields = filter.customPriceFields ?? EMPTY_CUSTOM_PRICE_FIELDS;

	let objectAreaTotal = 0;
	let classTotal = 0;
	classes.forEach(cls => {
		const objectArea = classificationResults[cls.id]?.objectArea || 0;
		objectAreaTotal += objectArea;
		classTotal += objectArea * (cls.pricePerM2 || 0);
	});
	// One flat total × price per field: summing the per-class object areas first
	// and then multiplying gives the same figure, so custom price fields stay a
	// single row each with no per-class breakdown beneath them.
	const customFieldsTotalCost = customPriceFields.reduce(
		(sum, f) => sum + objectAreaTotal * (f.price || 0),
		0
	);
	const grandTotal = classTotal + customFieldsTotalCost;

	// --- Class editing ---
	// Tracked by id rather than index: adding or removing a class shifts indices,
	// and an id survives that. `draft` holds the uncommitted edit.
	const [editingClassId, setEditingClassId] = useState(null);
	const [draft, setDraft] = useState(null);

	const startEditClass = cls => {
		setEditingClassId(cls.id);
		setDraft({ color: cls.color, label: cls.label, pricePerM2: cls.pricePerM2 ?? 0 });
	};

	const cancelEditClass = () => {
		setEditingClassId(null);
		setDraft(null);
	};

	const saveEditClass = () => {
		const index = classes.findIndex(c => c.id === editingClassId);
		if (index === -1 || !draft) return cancelEditClass();
		const label = draft.label?.trim();
		updateFilter(
			buildUpdateClassPatch(filter, index, {
				color: draft.color,
				// An empty label would leave the row and the map legend blank with
				// nothing to click back onto, so keep the previous one.
				label: label || classes[index].label,
				pricePerM2: draft.pricePerM2 ?? 0,
			})
		);
		cancelEditClass();
	};

	const removeClass = cls => {
		const index = classes.findIndex(c => c.id === cls.id);
		if (index === -1) return;
		if (editingClassId === cls.id) cancelEditClass();
		updateFilter(buildRemoveClassPatch(filter, index));
	};

	const addClass = () => {
		const { patch, newClass } = buildAddClassPatch(filter);
		updateFilter(patch);
		// Open the new row straight into edit mode — it exists only to be named and
		// priced, so this saves a round trip. Seeded from the class we just built
		// rather than read back from the store.
		setEditingClassId(newClass.id);
		setDraft({
			color: newClass.color,
			label: newClass.label,
			pricePerM2: newClass.pricePerM2,
		});
	};

	const updateCustomField = (idx, patch) =>
		updateFilter({
			customPriceFields: customPriceFields.map((f, i) =>
				i === idx ? { ...f, ...patch } : f
			),
		});
	const removeCustomField = idx =>
		updateFilter({ customPriceFields: customPriceFields.filter((_, i) => i !== idx) });
	const addCustomField = () =>
		updateFilter({
			customPriceFields: [
				...customPriceFields,
				{ id: createClassId('field'), label: DEFAULT_CUSTOM_PRICE_LABEL, price: 0 },
			],
		});

	// Row divider — a hairline between rows (not below the last one), matching
	// the reference layout's bordered list-inside-a-card look.
	const rowDivider = { borderBottom: `1px solid ${token.colorBorderSecondary}` };
	const rowStyle = { padding: '8px 0', ...rowDivider };

	return (
		<Flex vertical gap={6}>
			{/* Header: total object area and the currency every figure is priced in. */}
			<Flex align="center" justify="space-between" gap={12} wrap="wrap">
				<Flex align="baseline" gap={6}>
					<Text type="secondary" style={{ fontSize: '0.78rem' }}>
						Total object area
					</Text>
					{/* Unit scaling is off deliberately: the per-class rows below print
					    their area against a price fixed in {currency}/m² (`pricePerM2`),
					    so a scaled total would stop reconciling with the cost printed
					    beside it — which requirement 2.5 pins. Keeping the base unit also
					    matches the Classification Results table. */}
					<Text strong style={{ fontSize: '0.8rem' }}>
						{formatArea(objectAreaTotal, 1, false).join(' ')}
					</Text>
				</Flex>
				<Flex align="center" gap={6}>
					<Text type="secondary" style={{ fontSize: '0.75rem' }}>
						Currency
					</Text>
					{/* Flag + symbol only: the header is narrow and the full name
						    adds nothing the flag doesn't already say (and NOK/SEK share
						    "kr", so the flag is what distinguishes them). The unit
						    pseudo-currency has neither, so it keeps a short word. */}
					<Select
						size="small"
						value={filter.currency ?? DEFAULT_CURRENCY.id}
						onChange={cur => updateFilter({ currency: cur })}
						options={currencies.map(c => ({
							value: c.id,
							label: c.symbol ? `${c.flag ?? ''} ${c.symbol}`.trim() : 'Units',
							title: c.label,
						}))}
						style={{ minWidth: 80 }}
						aria-label="Pricing currency"
					/>
				</Flex>
			</Flex>

			<Card size="small" styles={{ body: { padding: '4px 12px' } }}>
				{classes.map(cls =>
					editingClassId === cls.id && draft ? (
						<ClassEditRow
							key={cls.id}
							draft={draft}
							setDraft={setDraft}
							currency={currency}
							onSave={saveEditClass}
							onCancel={cancelEditClass}
							style={rowStyle}
						/>
					) : (
						<ClassRow
							key={cls.id}
							cls={cls}
							objectArea={classificationResults[cls.id]?.objectArea || 0}
							price={cls.pricePerM2 || 0}
							currency={currency}
							onEdit={() => startEditClass(cls)}
							onRemove={() => removeClass(cls)}
							style={rowStyle}
						/>
					)
				)}

				{/* Below-minimum / unclassified — same special buckets shown in the
				    Classification Results table, called out here too since they're
				    excluded from cost totals but still explain the total area gap. */}
				{['below_minimum', 'unclassified']
					.map(id => classificationResults[id])
					.filter(r => r?.count > 0)
					.map((r, idx, arr) => (
						<Flex
							key={r.id}
							justify="space-between"
							gap={8}
							style={{
								padding: '8px 0',
								...(idx < arr.length - 1 || customPriceFields.length
									? rowDivider
									: {}),
							}}>
							<Text type="secondary" style={{ fontSize: '0.78rem' }}>
								<ColorDot color={r.color} />
								{r.label}
							</Text>
							<Text type="secondary" style={{ fontSize: '0.78rem' }}>
								{r.count} ({r.sharePercentage}%)
							</Text>
						</Flex>
					))}

				{customPriceFields.map((field, idx) => (
					<Flex
						key={field.id ?? idx}
						align="center"
						gap={6}
						wrap="wrap"
						style={{ padding: '8px 0', ...rowDivider }}>
						<Input
							size="small"
							value={field.label}
							onChange={e => updateCustomField(idx, { label: e.target.value })}
							style={{ flex: 1, minWidth: 100 }}
							aria-label={`Custom price label ${idx + 1}`}
							maxLength={40}
						/>
						<InputNumber
							size="small"
							value={field.price}
							onChange={price => updateCustomField(idx, { price: price ?? 0 })}
							min={0}
							step={0.1}
							placeholder="0.00"
							style={{ width: 90 }}
							aria-label={`Custom price per m² ${idx + 1}`}
							addonAfter={`${currency.symbol ?? ''}/m²`}
						/>
						<Button
							type="text"
							size="small"
							icon={<LuTrash2 />}
							onClick={() => removeCustomField(idx)}
							aria-label="Remove custom price"
						/>
					</Flex>
				))}
				{customPriceFields.length > 0 && customFieldsTotalCost > 0 && (
					<Flex justify="space-between" style={{ padding: '8px 0', ...rowDivider }}>
						<Text type="secondary" style={{ fontSize: '0.72rem', fontStyle: 'italic' }}>
							Custom prices subtotal
						</Text>
						<Text type="secondary" style={{ fontSize: '0.75rem' }}>
							{customFieldsTotalCost.toLocaleString(undefined, {
								maximumFractionDigits: 2,
							})}{' '}
							{currency.symbol}
						</Text>
					</Flex>
				)}
				{/* Both add actions share a row: a class is a priced band of the
				    classification, a custom price is a flat rate on the whole
				    classified area, and this is where either gets costed. */}
				<Flex align="center" gap={16} wrap="wrap" style={{ padding: '8px 0' }}>
					<Button
						size="small"
						type="link"
						icon={<LuPlus />}
						onClick={addClass}
						style={{ padding: 0, color: token.colorPrimary }}>
						Add class
					</Button>
					<Button
						size="small"
						type="link"
						icon={<LuPlus />}
						onClick={addCustomField}
						style={{ padding: 0, color: token.colorPrimary }}>
						Add custom price
					</Button>
				</Flex>
			</Card>

			<Flex justify="space-between" style={{ padding: '4px 4px 0' }}>
				<Text strong style={{ fontSize: '0.85rem' }}>
					Total
				</Text>
				<Text strong style={{ fontSize: '0.85rem', color: token.colorPrimary }}>
					{grandTotal.toLocaleString(undefined, { maximumFractionDigits: 2 })}{' '}
					{currency.symbol}
				</Text>
			</Flex>
		</Flex>
	);
};

export default CostSummary;
